import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Supplier, Warehouse, UserPermissions, ProductTagSetting } from '../types';
import '../core.css';
import LoadingSpinner from './LoadingSpinner';
import TaskNotificationSettings from './tasks/TaskNotificationSettings';
import HolidaysSettings from './tasks/HolidaysSettings';
import UserColorPicker from './UserColorPicker';
import { useOptionalTasks } from '../context/TasksContext';

const useBeforeUnload = (shouldPrompt: boolean) => {
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldPrompt) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes in the admin settings. Do you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldPrompt]);
};

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items: { id: number; name: string }[];
  onSelect: (name: string) => void;
}

const Dialog: React.FC<DialogProps> = ({ isOpen, onClose, title, items, onSelect }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="supplier-dialog-overlay">
      <div className="supplier-dialog">
        <h3>{title}</h3>
        <ul className="supplier-list">
          {items.map((item) => (
            <li
              key={item.id}
              className="supplier-item"
              onClick={() => onSelect(item.name)}
            >
              {item.name}
            </li>
          ))}
        </ul>
        <div className="supplier-dialog-actions">
          <button className="action-button cancel-button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Admin: React.FC = () => {
  const { user, logout } = useAuth();
  const tasks = useOptionalTasks();
  const [newEmail, setNewEmail] = useState('');
  const [newSupplier, setNewSupplier] = useState('');
  const [newWarehouse, setNewWarehouse] = useState('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermissions[]>([]);
  const [productTags, setProductTags] = useState<ProductTagSetting[]>([]);
  const [productTagError, setProductTagError] = useState<string | null>(null);
  const [profilePictureFailed, setProfilePictureFailed] = useState(false);
  const [supplierError, setSupplierError] = useState<string | null>(null);
  const [warehouseError, setWarehouseError] = useState<string | null>(null);
  const [editedSuppliers, setEditedSuppliers] = useState<Supplier[]>([]);
  const [editedWarehouses, setEditedWarehouses] = useState<Warehouse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [columnSettings, setColumnSettings] = useState<{ column_name: string; is_visible: boolean }[]>([]);
  const [columnError, setColumnError] = useState<string | null>(null);
  const [lockGtinFields, setLockGtinFields] = useState(false);
  const [cusmaDocument, setCusmaDocument] = useState<string | null>(null);
  const [cusmaFileName, setCusmaFileName] = useState<string | null>(null);
  const [defaultShipmentPhone, setDefaultShipmentPhone] = useState('');
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<{
    type: 'supplier' | 'warehouse' | null;
    email: string | null;
  }>({ type: null, email: null });
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const lastPathnameRef = useRef(currentLocation.pathname);
  const initialSuppliersRef = useRef<Supplier[]>([]);
  const initialWarehousesRef = useRef<Warehouse[]>([]);
  const initialColumnSettingsRef = useRef<{ column_name: string; is_visible: boolean }[]>([]);
  const initialLockGtinFieldsRef = useRef(false);
  const initialCusmaDocumentRef = useRef<string | null>(null);
  const initialCusmaFileNameRef = useRef<string | null>(null);
  const initialDefaultShipmentPhoneRef = useRef('');
  const initialUserPermissionsRef = useRef<UserPermissions[]>([]);
  const initialProductTagsRef = useRef<ProductTagSetting[]>([]);

  const hasUnsavedChanges =
    newEmail.trim() !== '' ||
    newSupplier.trim() !== '' ||
    newWarehouse.trim() !== '' ||
    editedSuppliers.some((s, i) => s.name !== initialSuppliersRef.current[i]?.name || s.color !== initialSuppliersRef.current[i]?.color) ||
    editedWarehouses.some((w, i) =>
      w.name !== initialWarehousesRef.current[i]?.name ||
      w.color !== initialWarehousesRef.current[i]?.color ||
      w.barcode_shipping !== initialWarehousesRef.current[i]?.barcode_shipping ||
      w.barcode_production !== initialWarehousesRef.current[i]?.barcode_production
    ) ||
    columnSettings.some((c, i) => c.is_visible !== initialColumnSettingsRef.current[i]?.is_visible) ||
    lockGtinFields !== initialLockGtinFieldsRef.current ||
    cusmaDocument !== initialCusmaDocumentRef.current ||
    cusmaFileName !== initialCusmaFileNameRef.current ||
    defaultShipmentPhone !== initialDefaultShipmentPhoneRef.current ||
    productTags.some((t, i) => t.use_for_analytics !== initialProductTagsRef.current[i]?.use_for_analytics) ||
    userPermissions.some((u, i) =>
      u.user_type !== initialUserPermissionsRef.current[i]?.user_type ||
      u.home_page !== initialUserPermissionsRef.current[i]?.home_page ||
      u.orders_page !== initialUserPermissionsRef.current[i]?.orders_page ||
      u.barcode_production !== initialUserPermissionsRef.current[i]?.barcode_production ||
      u.barcode_shipping !== initialUserPermissionsRef.current[i]?.barcode_shipping ||
      u.user_color !== initialUserPermissionsRef.current[i]?.user_color ||
      (u.display_name || '') !== (initialUserPermissionsRef.current[i]?.display_name || '') ||
      JSON.stringify(u.visible_suppliers) !== JSON.stringify(initialUserPermissionsRef.current[i]?.visible_suppliers) ||
      JSON.stringify(u.editable_warehouses) !== JSON.stringify(initialUserPermissionsRef.current[i]?.editable_warehouses)
    );
  useBeforeUnload(hasUnsavedChanges);

  useEffect(() => {
    if (lastPathnameRef.current === currentLocation.pathname) {
      return;
    }

    if (hasUnsavedChanges) {
      const shouldLeave = window.confirm(
        'You have unsaved changes in the admin settings. Do you want to save changes, cancel, or leave? Click OK to leave (changes will be discarded).'
      );
      if (!shouldLeave) {
        navigate(lastPathnameRef.current, { replace: true });
        return;
      }
      setNewEmail('');
      setNewSupplier('');
      setNewWarehouse('');
      setEditedSuppliers([...suppliers]);
      setEditedWarehouses([...warehouses]);
      setColumnSettings([...initialColumnSettingsRef.current]);
      setLockGtinFields(initialLockGtinFieldsRef.current);
      setDefaultShipmentPhone(initialDefaultShipmentPhoneRef.current);
      setUserPermissions([...initialUserPermissionsRef.current]);
      setProductTags([...initialProductTagsRef.current]);
    }

    lastPathnameRef.current = currentLocation.pathname;
  }, [currentLocation.pathname, newEmail, newSupplier, newWarehouse, editedSuppliers, editedWarehouses, columnSettings, lockGtinFields, userPermissions, navigate, suppliers, warehouses]);

  const fetchSuppliersAndWarehouses = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/neon-stock');
      if (!response.data || !Array.isArray(response.data.suppliers) || !Array.isArray(response.data.warehouses)) {
        throw new Error('Invalid response structure from /neon-stock');
      }
      const fetchedSuppliers = response.data.suppliers.map((s: any) => ({
        id: s.id,
        name: s.name,
        color: s.color || '#e0e0e0',
      }));
      setSuppliers(fetchedSuppliers);
      setEditedSuppliers(fetchedSuppliers);
      initialSuppliersRef.current = fetchedSuppliers;
      setSupplierError(null);

      const fetchedWarehouses = response.data.warehouses.map((w: any) => ({
        id: w.id,
        name: w.name,
        color: w.color || '#e0e0e0',
        barcode_shipping: w.barcode_shipping || false,
        barcode_production: w.barcode_production || false,
      }));
      setWarehouses(fetchedWarehouses);
      setEditedWarehouses(fetchedWarehouses);
      initialWarehousesRef.current = fetchedWarehouses;
      setWarehouseError(null);
    } catch (error: any) {
      console.error('Fetch suppliers/warehouses error:', error.message);
      setSupplierError(error.response?.data?.error || 'Failed to fetch suppliers');
      setWarehouseError(error.response?.data?.error || 'Failed to fetch warehouses');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserPermissions = async () => {
    try {
      const response = await api.get('/user-permissions');
      const users = response.data.users.map((u: UserPermissions) => ({
        ...u,
        home_page: u.user_type === 'Admin' ? true : u.home_page,
        orders_page: u.user_type === 'Admin' ? true : u.orders_page || false,
        barcode_production: u.user_type === 'Admin' ? true : u.barcode_production || false,
        barcode_shipping: u.user_type === 'Admin' ? true : u.barcode_shipping || false,
        user_color: u.user_color || '#f8f9fa',
      }));
      setUserPermissions(users);
      initialUserPermissionsRef.current = users;
    } catch (error: any) {
      console.error('Fetch user permissions error:', error.message);
    }
  };

  const fetchProductTags = async () => {
    try {
      const response = await api.get('/product-tags');
      const tags: ProductTagSetting[] = (response.data.tags || []).map((t: any) => ({
        id: t.id,
        tag_name: t.tag_name,
        color: t.color || '#e0e0e0',
        use_for_analytics: t.use_for_analytics || false,
      }));
      setProductTags(tags);
      initialProductTagsRef.current = tags;
      setProductTagError(null);
    } catch (error: any) {
      console.error('Fetch product tags error:', error.message);
      setProductTagError(error.response?.data?.error || 'Failed to fetch product tags');
    }
  };

  const fetchColumnSettings = async () => {
    try {
      const response = await api.get('/column-settings');
      let columns = response.data.columns;

      // Ensure new columns are present
      const requiredColumns = ['product_message', 'estimated_restock_date'];
      requiredColumns.forEach(colName => {
        if (!columns.find((c: any) => c.column_name === colName)) {
          columns.push({ column_name: colName, is_visible: true });
        }
      });

      setColumnSettings(columns);
      initialColumnSettingsRef.current = columns;
      setColumnError(null);
    } catch (error: any) {
      console.error('Fetch column settings error:', error.message);
      setColumnError(error.response?.data?.error || 'Failed to fetch column settings');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setLockGtinFields(response.data.lock_gtin_fields);
      setCusmaDocument(response.data.cusma_document);
      setCusmaFileName(response.data.cusma_file_name);
      setDefaultShipmentPhone(response.data.default_shipment_phone || '');
      initialDefaultShipmentPhoneRef.current = response.data.default_shipment_phone || '';
      initialLockGtinFieldsRef.current = response.data.lock_gtin_fields;
      initialCusmaDocumentRef.current = response.data.cusma_document;
      initialCusmaFileNameRef.current = response.data.cusma_file_name;
      setSettingsError(null);
    } catch (error: any) {
      console.error('Fetch settings error:', error.message);
      setSettingsError(error.response?.data?.error || 'Failed to fetch settings');
    }
  };

  useEffect(() => {
    fetchSuppliersAndWarehouses();
    fetchUserPermissions();
    fetchProductTags();
    fetchColumnSettings();
    fetchSettings();
  }, []);

  const handleAddEmail = async () => {
    if (!newEmail.trim() || !newEmail.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }
    try {
      await api.post('/user-permissions', {
        users: [{
          email: newEmail.trim(),
          user_type: 'Regular',
          home_page: false,
          orders_page: false,
          barcode_production: false,
          barcode_shipping: false,
          user_color: '#f8f9fa',
          visible_suppliers: [],
          editable_warehouses: [],
        }],
      });
      setNewEmail('');
      await fetchUserPermissions();
    } catch (error: any) {
      console.error('Add user error:', error.message);
      alert(error.response?.data?.error || 'Failed to add user. Please try again.');
    }
  };

  const handleAddSupplier = async () => {
    if (!newSupplier.trim()) {
      setSupplierError('Supplier name cannot be empty');
      return;
    }

    try {
      const response = await api.post('/add-supplier', { name: newSupplier.trim() });
      const newSupplierData = { ...response.data.supplier, color: response.data.supplier.color || '#e0e0e0' };
      setSuppliers([...suppliers, newSupplierData]);
      setEditedSuppliers([...editedSuppliers, newSupplierData]);
      initialSuppliersRef.current = [...initialSuppliersRef.current, newSupplierData];
      setNewSupplier('');
      setSupplierError(null);
    } catch (error: any) {
      console.error('Add supplier error:', error.message);
      setSupplierError(error.response?.data?.error || 'Failed to add supplier');
    }
  };

  const handleAddWarehouse = async () => {
    if (!newWarehouse.trim()) {
      setWarehouseError('Warehouse name cannot be empty');
      return;
    }

    try {
      const response = await api.post('/add-warehouse', { name: newWarehouse.trim() });
      const newWarehouseData = {
        ...response.data.warehouse,
        color: response.data.warehouse.color || '#e0e0e0',
        barcode_shipping: false,
        barcode_production: false,
      };
      setWarehouses([...warehouses, newWarehouseData]);
      setEditedWarehouses([...editedWarehouses, newWarehouseData]);
      initialWarehousesRef.current = [...initialWarehousesRef.current, newWarehouseData];
      setNewWarehouse('');
      setWarehouseError(null);
    } catch (error: any) {
      console.error('Add warehouse error:', error.message);
      setWarehouseError(error.response?.data?.error || 'Failed to add warehouse');
    }
  };

  const handleRemoveSupplier = async (id: number) => {
    const supplier = suppliers.find((s) => s.id === id);
    if (!supplier) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the supplier "${supplier.name}"? This action cannot be undone, and all associated database values will be permanently deleted.`
    );
    if (!confirmDelete) return;

    try {
      await api.delete(`/delete-supplier?id=${id}`);
      setSuppliers(suppliers.filter((supplier) => supplier.id !== id));
      setEditedSuppliers(editedSuppliers.filter((supplier) => supplier.id !== id));
      initialSuppliersRef.current = initialSuppliersRef.current.filter((supplier) => supplier.id !== id);
      setSupplierError(null);
    } catch (error: any) {
      console.error('Delete supplier error:', error.message);
      setSupplierError(error.response?.data?.error || 'Failed to delete supplier');
    }
  };

  const handleRemoveWarehouse = async (id: number) => {
    const warehouse = warehouses.find((w) => w.id === id);
    if (!warehouse) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the warehouse "${warehouse.name}"? This action cannot be undone. Warehouse stock will be deleted, and historical stock levels will be moved to a "deprecated" warehouse.`
    );
    if (!confirmDelete) return;

    try {
      await api.delete(`/delete-warehouse?id=${id}`);
      setWarehouses(warehouses.filter((warehouse) => warehouse.id !== id));
      setEditedWarehouses(editedWarehouses.filter((warehouse) => warehouse.id !== id));
      initialWarehousesRef.current = initialWarehousesRef.current.filter((warehouse) => warehouse.id !== id);
      setWarehouseError(null);
    } catch (error: any) {
      console.error('Delete warehouse error:', error.message);
      setWarehouseError(error.response?.data?.error || 'Failed to delete warehouse');
    }
  };

  const handleSupplierChange = (id: number, field: 'name' | 'color', value: string) => {
    setEditedSuppliers(
      editedSuppliers.map((supplier) =>
        supplier.id === id ? { ...supplier, [field]: value } : supplier
      )
    );
  };

  const handleWarehouseChange = (id: number, field: 'name' | 'color' | 'barcode_shipping' | 'barcode_production', value: string | boolean) => {
    setEditedWarehouses(
      editedWarehouses.map((warehouse) =>
        warehouse.id === id ? { ...warehouse, [field]: value } : warehouse
      )
    );
  };

  const handleSaveSuppliers = async () => {
    try {
      for (const supplier of editedSuppliers) {
        if (!supplier.color || !/^#[0-9A-Fa-f]{6}$/.test(supplier.color)) {
          throw new Error(`Invalid hex color for supplier ${supplier.name}: ${supplier.color}`);
        }
      }
      await api.post('/update-suppliers', { suppliers: editedSuppliers });
      setSuppliers([...editedSuppliers]);
      initialSuppliersRef.current = [...editedSuppliers];
      setSupplierError(null);
      alert('Suppliers updated successfully');
    } catch (error: any) {
      console.error('Update suppliers error:', error.message);
      setSupplierError(error.response?.data?.error || error.message);
    }
  };

  const handleSaveWarehouses = async () => {
    try {
      for (const warehouse of editedWarehouses) {
        if (!warehouse.color || !/^#[0-9A-Fa-f]{6}$/.test(warehouse.color)) {
          throw new Error(`Invalid hex color for warehouse ${warehouse.name}: ${warehouse.color}`);
        }
      }
      await api.post('/update-warehouses', { warehouses: editedWarehouses });
      setWarehouses([...editedWarehouses]);
      initialWarehousesRef.current = [...editedWarehouses];
      setWarehouseError(null);
      alert('Warehouses updated successfully');
    } catch (error: any) {
      console.error('Update warehouses error:', error.message);
      setWarehouseError(error.response?.data?.error || error.message);
    }
  };

  const handlePermissionChange = (email: string, field: keyof UserPermissions, value: any) => {
    if (
      userPermissions.find((u) => u.email === email)?.user_type === 'Admin' &&
      field !== 'user_color' &&
      field !== 'display_name'
    ) return;
    setUserPermissions(
      userPermissions.map((u) =>
        u.email === email ? { ...u, [field]: value } : u
      )
    );
  };

  const handleSaveUserPermissions = async () => {
    try {
      await api.post('/user-permissions', { users: userPermissions });
      initialUserPermissionsRef.current = [...userPermissions];
      // Renamed people show up on task initials straight away.
      tasks?.refresh();
      alert('User permissions updated successfully');
    } catch (error: any) {
      console.error('Update user permissions error:', error.message);
      alert('Failed to update user permissions');
    }
  };

  const handleProductTagAnalyticsToggle = (id: number) => {
    setProductTags(
      productTags.map((tag) =>
        tag.id === id ? { ...tag, use_for_analytics: !tag.use_for_analytics } : tag
      )
    );
  };

  const handleSaveProductTags = async () => {
    try {
      await api.put('/product-tags', {
        tags: productTags.map((tag) => ({ id: tag.id, use_for_analytics: tag.use_for_analytics })),
      });
      initialProductTagsRef.current = [...productTags];
      setProductTagError(null);
      alert('Product tags updated successfully');
    } catch (error: any) {
      console.error('Update product tags error:', error.message);
      setProductTagError(error.response?.data?.error || error.message);
    }
  };

  const handleColumnToggle = (columnName: string) => {
    setColumnSettings(
      columnSettings.map((col) =>
        col.column_name === columnName ? { ...col, is_visible: !col.is_visible } : col
      )
    );
  };

  const handleSaveColumnSettings = async () => {
    try {
      await api.post('/column-settings', { columns: columnSettings });
      initialColumnSettingsRef.current = [...columnSettings];
      setColumnError(null);
      alert('Column settings updated successfully');
    } catch (error: any) {
      console.error('Update column settings error:', error.message);
      setColumnError(error.response?.data?.error || error.message);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.post('/settings', { lock_gtin_fields: lockGtinFields, cusma_document: cusmaDocument, cusma_file_name: cusmaFileName, default_shipment_phone: defaultShipmentPhone });
      initialLockGtinFieldsRef.current = lockGtinFields;
      initialDefaultShipmentPhoneRef.current = defaultShipmentPhone;
      initialCusmaDocumentRef.current = cusmaDocument;
      initialCusmaFileNameRef.current = cusmaFileName;
      setSettingsError(null);
      alert('Settings updated successfully');
    } catch (error: any) {
      console.error('Update settings error:', error.message);
      setSettingsError(error.response?.data?.error || error.message);
    }
  };

  const handleGenerateToken = async () => {
    try {
      const response = await api.post('/fdc/generatetoken');
      console.log('Generate Token response:', response.data);
    } catch (error: any) {
      console.error('Generate Token error:', error);
    }
  };

  const handleGetInventory = async () => {
    try {
      const response = await api.get('/fdc/getinventory');
      console.log('Get Inventory response:', response.data);
    } catch (error: any) {
      console.error('Get Inventory error:', error);
    }
  };

  const handleTestFreightcomPaymentMethods = async () => {
    try {
      const response = await api.get('/freightcom/testpayment');
      console.log('Freightcom Payment Methods response:', response.data);
    } catch (error: any) {
      console.error('Freightcom Payment Methods error:', error);
    }
  };

  const handleTestFreightcomShipmentRate = async () => {
    try {
      const response = await api.get('/freightcom/testshipmentrate');
      console.log('Freightcom Shipment Rate test response:', response.data);
    } catch (error: any) {
      console.error('Freightcom Shipment Rate test error:', error);
    }
  };

  const handleCusmaDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please upload a PDF file');
        return;
      }
      setCusmaFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = (e.target?.result as string).split(',')[1];
        setCusmaDocument(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCusmaDocumentDownload = () => {
    if (cusmaDocument && cusmaFileName) {
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${cusmaDocument}`;
      link.download = cusmaFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCusmaDocumentDelete = () => {
    if (window.confirm('Are you sure you want to delete the CUSMA document? This action cannot be undone.')) {
      setCusmaDocument(null);
      setCusmaFileName(null);
    }
  };

  const handleSupplierDialogSelect = (email: string, supplierName: string) => {
    if (userPermissions.find((u) => u.email === email)?.user_type === 'Admin') return;
    setUserPermissions(
      userPermissions.map((u) =>
        u.email === email
          ? { ...u, visible_suppliers: [...u.visible_suppliers, supplierName] }
          : u
      )
    );
    setDialogState({ type: null, email: null });
  };

  const handleWarehouseDialogSelect = (email: string, warehouseName: string) => {
    if (userPermissions.find((u) => u.email === email)?.user_type === 'Admin') return;
    setUserPermissions(
      userPermissions.map((u) =>
        u.email === email
          ? { ...u, editable_warehouses: [...u.editable_warehouses, warehouseName] }
          : u
      )
    );
    setDialogState({ type: null, email: null });
  };

  const handleRemoveVisibleSupplier = (email: string, supplierName: string) => {
    if (userPermissions.find((u) => u.email === email)?.user_type === 'Admin') return;
    setUserPermissions(
      userPermissions.map((u) =>
        u.email === email
          ? { ...u, visible_suppliers: u.visible_suppliers.filter((s) => s !== supplierName) }
          : u
      )
    );
  };

  const handleRemoveEditableWarehouse = (email: string, warehouseName: string) => {
    if (userPermissions.find((u) => u.email === email)?.user_type === 'Admin') return;
    setUserPermissions(
      userPermissions.map((u) =>
        u.email === email
          ? { ...u, editable_warehouses: u.editable_warehouses.filter((w) => w !== warehouseName) }
          : u
      )
    );
  };

  const handleRemoveUser = async (email: string) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the user "${email}"? This action cannot be undone, and all associated database values will be permanently deleted.`
    );
    if (!confirmDelete) return;

    try {
      await api.delete('/user-permissions', { data: { email } });
      await fetchUserPermissions();
      if (user && user.email === email) {
        logout();
      }
    } catch (error: any) {
      console.error('Remove user error:', error);
      alert(error.response?.data?.error || 'Failed to remove user. Please try again.');
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="container admin-page">
      <h1>Admin</h1>
      <div className="admin-content-container">
        {user && (
          <>
            <h2 className="section-heading">Profile</h2>
            <div className="profile-info">
              {/* Google serves the avatar from lh3.googleusercontent.com, which 403s when a
                  referrer is sent, and the URL expires for long-lived sessions — hence both the
                  no-referrer policy and the initials fallback. */}
              {user.picture && !profilePictureFailed ? (
                <img
                  src={user.picture}
                  alt={`${user.name} profile`}
                  className="profile-picture"
                  referrerPolicy="no-referrer"
                  onError={() => setProfilePictureFailed(true)}
                />
              ) : (
                <div className="profile-picture profile-picture-fallback" aria-hidden="true">
                  {(user.name || user.email || '?').trim().charAt(0).toUpperCase()}
                </div>
              )}
              <div className="profile-details">
                <p className="profile-name">Welcome, {user.name}!</p>
                <p className="profile-email">Email: {user.email}</p>
              </div>
            </div>
          </>
        )}
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Users</h2>
          <p className="section-description">Manage user permissions and access.</p>
          <div className="admin-add-section">
            <input
              type="text"
              placeholder="Add user email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="admin-input"
            />
            <button onClick={handleAddEmail} className="action-button">
              Add
            </button>
          </div>
          <div className="admin-table-scroll">
          <table className="admin-table user-permissions-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>User Type</th>
                <th>Home Page</th>
                <th>Orders Page</th>
                <th>Barcode Production</th>
                <th>Order Processing</th>
                <th>Color</th>
                <th>Visible Suppliers</th>
                <th>Active Warehouses</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userPermissions.map((u) => {
                const isAdminUser = u.user_type === 'Admin';
                return (
                  <tr key={u.email}>
                    <td className="email-cell">{u.email}</td>
                    <td>
                      {/* Shown on task initials and "assigned by". Filled from Google on first
                          sign-in; once set here, sign-in leaves it alone. Blank = Google name
                          again at next sign-in. */}
                      <input
                        type="text"
                        value={u.display_name || ''}
                        onChange={(e) => handlePermissionChange(u.email, 'display_name', e.target.value)}
                        className="user-name-input"
                        placeholder="From Google"
                        aria-label={`Display name for ${u.email}`}
                      />
                    </td>
                    <td>
                      <select
                        value={u.user_type}
                        onChange={(e) => handlePermissionChange(u.email, 'user_type', e.target.value)}
                        disabled={isAdminUser}
                        className="user-type-select"
                      >
                        <option value="Admin">Admin</option>
                        <option value="Regular">Regular</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={isAdminUser || u.home_page}
                        onChange={(e) => handlePermissionChange(u.email, 'home_page', e.target.checked)}
                        disabled={isAdminUser}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={isAdminUser || u.orders_page}
                        onChange={(e) => handlePermissionChange(u.email, 'orders_page', e.target.checked)}
                        disabled={isAdminUser}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={isAdminUser || u.barcode_production}
                        onChange={(e) => handlePermissionChange(u.email, 'barcode_production', e.target.checked)}
                        disabled={isAdminUser}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={isAdminUser || u.barcode_shipping}
                        onChange={(e) => handlePermissionChange(u.email, 'barcode_shipping', e.target.checked)}
                        disabled={isAdminUser}
                      />
                    </td>
                    <td>
                      <UserColorPicker
                        value={u.user_color}
                        label={u.display_name || u.email}
                        onChange={(color) => handlePermissionChange(u.email, 'user_color', color)}
                      />
                    </td>
                    <td>
                      <div className="supplier-tags">
                        {isAdminUser ? (
                          <span className="supplier-tag all">ALL</span>
                        ) : (
                          <>
                            {u.visible_suppliers.map((supplierName) => {
                              const supplier = suppliers.find((s) => s.name === supplierName);
                              return (
                                <span
                                  key={supplierName}
                                  className="supplier-tag"
                                  style={{ backgroundColor: supplier?.color || '#e0e0e0' }}
                                >
                                  {supplierName}
                                  <button
                                    className="email-tag-remove"
                                    onClick={() => handleRemoveVisibleSupplier(u.email, supplierName)}
                                  >
                                    ✕
                                  </button>
                                </span>
                              );
                            })}
                            <span
                              className="supplier-tag add-tag"
                              onClick={() => setDialogState({ type: 'supplier', email: u.email })}
                            >
                              +
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="supplier-tags">
                        {isAdminUser ? (
                          <span className="supplier-tag all">ALL</span>
                        ) : (
                          <>
                            {u.editable_warehouses.map((warehouseName) => {
                              const warehouse = warehouses.find((w) => w.name === warehouseName);
                              return (
                                <span
                                  key={warehouseName}
                                  className="supplier-tag"
                                  style={{ backgroundColor: warehouse?.color || '#e0e0e0' }}
                                >
                                  {warehouseName}
                                  <button
                                    className="email-tag-remove"
                                    onClick={() => handleRemoveEditableWarehouse(u.email, warehouseName)}
                                  >
                                    ✕
                                  </button>
                                </span>
                              );
                            })}
                            <span
                              className="supplier-tag add-tag"
                              onClick={() => setDialogState({ type: 'warehouse', email: u.email })}
                            >
                              +
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        className="action-button delete-button"
                        onClick={() => handleRemoveUser(u.email)}
                        disabled={u.email === 'sanis@mountainpassperformance.com'}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {dialogState.type === 'supplier' && dialogState.email && (
            <Dialog
              isOpen={true}
              onClose={() => setDialogState({ type: null, email: null })}
              title="Select Supplier"
              items={suppliers.filter((s) => !userPermissions.find((u) => u.email === dialogState.email)?.visible_suppliers.includes(s.name))}
              onSelect={(name) => handleSupplierDialogSelect(dialogState.email!, name)}
            />
          )}
          {dialogState.type === 'warehouse' && dialogState.email && (
            <Dialog
              isOpen={true}
              onClose={() => setDialogState({ type: null, email: null })}
              title="Select Warehouse"
              items={warehouses.filter((w) => !userPermissions.find((u) => u.email === dialogState.email)?.editable_warehouses.includes(w.name))}
              onSelect={(name) => handleWarehouseDialogSelect(dialogState.email!, name)}
            />
          )}
          <button onClick={handleSaveUserPermissions} className="action-button admin-save-button">
            Save User Permissions
          </button>
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Suppliers</h2>
          <p className="section-description">Manage the list of suppliers for products.</p>
          {supplierError && <p className="error-text">{supplierError}</p>}
          <div className="admin-add-section">
            <input
              type="text"
              placeholder="Add supplier"
              value={newSupplier}
              onChange={(e) => setNewSupplier(e.target.value)}
              className="admin-input"
            />
            <button onClick={handleAddSupplier} className="action-button">
              Add
            </button>
          </div>
          <table className="admin-table supplier-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Background Color</th>
                <th>Color Preview</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {editedSuppliers.map((supplier) => (
                <tr key={supplier.id}>
                  <td>
                    <input
                      type="text"
                      value={supplier.name}
                      onChange={(e) => handleSupplierChange(supplier.id, 'name', e.target.value)}
                      className="supplier-input"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={supplier.color}
                      onChange={(e) => handleSupplierChange(supplier.id, 'color', e.target.value)}
                      className="supplier-input"
                      placeholder="#RRGGBB"
                    />
                  </td>
                  <td>
                    <span
                      className="color-preview"
                      style={{ backgroundColor: supplier.color }}
                    ></span>
                  </td>
                  <td>
                    <button
                      className="action-button delete-button"
                      onClick={() => handleRemoveSupplier(supplier.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={handleSaveSuppliers} className="action-button admin-save-button">
            Save Suppliers
          </button>
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Product Tags</h2>
          <p className="section-description">
            Tags cover both the vehicle model a product fits and the type of part it is. Tick
            "Use for analytics grouping" on the tags that should form the top level of the
            "Sales By Product Tag" chart — every other tag appears when you drill into one of
            them. Tags themselves are created and assigned on the Stock page.
          </p>
          {productTagError && <p className="error-text">{productTagError}</p>}
          <table className="admin-table product-tag-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Color Preview</th>
                <th>Use for analytics grouping</th>
              </tr>
            </thead>
            <tbody>
              {productTags.length === 0 ? (
                <tr>
                  <td colSpan={3}>No product tags found.</td>
                </tr>
              ) : (
                productTags.map((tag) => (
                  <tr key={tag.id}>
                    <td>{tag.tag_name}</td>
                    <td>
                      <span className="color-preview" style={{ backgroundColor: tag.color }}></span>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={tag.use_for_analytics}
                        onChange={() => handleProductTagAnalyticsToggle(tag.id)}
                        aria-label={`Use ${tag.tag_name} for analytics grouping`}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <button onClick={handleSaveProductTags} className="action-button admin-save-button">
            Save Product Tags
          </button>
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Warehouses</h2>
          <p className="section-description">Manage the list of warehouses for stock management.</p>
          {warehouseError && <p className="error-text">{warehouseError}</p>}
          <div className="admin-add-section">
            <input
              type="text"
              placeholder="Add warehouse"
              value={newWarehouse}
              onChange={(e) => setNewWarehouse(e.target.value)}
              className="admin-input"
            />
            <button onClick={handleAddWarehouse} className="action-button">
              Add
            </button>
          </div>
          <table className="admin-table warehouse-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Background Color</th>
                <th>Color Preview</th>
                <th>Barcode Orders</th>
                <th>Barcode Inventory</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {editedWarehouses.map((warehouse) => (
                <tr key={warehouse.id}>
                  <td>
                    <input
                      type="text"
                      value={warehouse.name}
                      onChange={(e) => handleWarehouseChange(warehouse.id, 'name', e.target.value)}
                      className="warehouse-input"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={warehouse.color}
                      onChange={(e) => handleWarehouseChange(warehouse.id, 'color', e.target.value)}
                      className="warehouse-input"
                      placeholder="#RRGGBB"
                    />
                  </td>
                  <td>
                    <span
                      className="color-preview"
                      style={{ backgroundColor: warehouse.color }}
                    ></span>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={warehouse.barcode_shipping}
                      onChange={(e) => handleWarehouseChange(warehouse.id, 'barcode_shipping', e.target.checked)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={warehouse.barcode_production}
                      onChange={(e) => handleWarehouseChange(warehouse.id, 'barcode_production', e.target.checked)}
                    />
                  </td>
                  <td>
                    <button
                      className="action-button delete-button"
                      onClick={() => handleRemoveWarehouse(warehouse.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={handleSaveWarehouses} className="action-button admin-save-button">
            Save Warehouses
          </button>
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Stock Table Display Columns</h2>
          <p className="section-description">Choose which columns to display in the stock table.</p>
          {columnError && <p className="error-text">{columnError}</p>}
          <div className="column-settings">
            {columnSettings.map((col) => (
              <div key={col.column_name} className="column-checkbox">
                <input
                  type="checkbox"
                  checked={col.is_visible}
                  onChange={() => handleColumnToggle(col.column_name)}
                  id={col.column_name}
                />
                <label className="checkbox-label" htmlFor={col.column_name}>
                  {col.column_name
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </label>
              </div>
            ))}
          </div>
          <button onClick={handleSaveColumnSettings} className="action-button admin-save-button">
            Save Column Settings
          </button>
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Customs Details</h2>
          <p className="section-description">Manage customs-related documents and settings.</p>
          {settingsError && <p className="error-text">{settingsError}</p>}
          <div className="settings-section">
            <div className="cusma-upload-section">
              <label
                className="cusma-label"
                style={{
                  color: '#666',
                  display: 'inline-block',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                  fontSize: '14px',
                  fontWeight: '400',
                  lineHeight: '1'
                }}
              >
                CUSMA Document (PDF):
              </label>
              {!cusmaDocument ? (
                <>
                  <span style={{ marginLeft: '10px' }}></span>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleCusmaDocumentUpload}
                    className="cusma-input"
                  />
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
                  <span style={{
                    marginRight: '10px',
                    marginLeft: '20px',
                    color: '#666',
                    display: 'inline-block',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '1'
                  }}>{cusmaFileName}</span>
                  <button
                    onClick={handleCusmaDocumentDownload}
                    style={{
                      marginRight: '10px',
                      background: 'none',
                      border: 'none',
                      color: '#007bff',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'
                    }}
                  >
                    Download
                  </button>
                  <button
                    onClick={handleCusmaDocumentDelete}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'red',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
            <button onClick={handleSaveSettings} className="action-button admin-save-button">
              Save Customs Details
            </button>
          </div>
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Settings</h2>
          <p className="section-description">
            Here you can manage app settings (e.g., toggle features, update preferences).
          </p>
          {settingsError && <p className="error-text">{settingsError}</p>}
          <div className="settings-section">
            <div className="column-checkbox">
              <input
                type="checkbox"
                checked={lockGtinFields}
                onChange={(e) => setLockGtinFields(e.target.checked)}
                id="lock_gtin_fields"
              />
              <label className="checkbox-label" htmlFor="lock_gtin_fields">Lock GTIN Fields</label>
            </div>
            <div className="admin-setting-field">
              <label htmlFor="default_shipment_phone">Default Phone Number for Shipments</label>
              <input
                id="default_shipment_phone"
                type="tel"
                value={defaultShipmentPhone}
                onChange={(e) => setDefaultShipmentPhone(e.target.value)}
                placeholder="e.g. 555-555-5555"
              />
              <p className="section-description">
                Filled into the Freightcom label's phone field when the order has no customer phone.
              </p>
            </div>
            <button onClick={handleSaveSettings} className="action-button admin-save-button">
              Save Settings
            </button>
          </div>
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Notifications</h2>
          <p className="section-description">
            Who receives each kind of automatic task, and who gets the morning task email.
          </p>
          <TaskNotificationSettings />
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Holidays</h2>
          <p className="section-description">
            Days the business is closed. No morning task email goes out on them, and a reminder to set the
            website / voicemail holiday notice goes on the week list ahead of each one.
          </p>
          <HolidaysSettings />
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">FDC APIs</h2>
          <p className="section-description">Test FDC APIs.</p>
          <button onClick={handleGenerateToken} className="action-button">Generate Token</button>
          <button onClick={handleGetInventory} className="action-button">Get Inventory</button>
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Freightcom APIs</h2>
          <p className="section-description">Test Freightcom API endpoints.</p>
          <button onClick={handleTestFreightcomPaymentMethods} className="action-button">Test Payment Methods</button>
          <button onClick={handleTestFreightcomShipmentRate} className="action-button">Test Shipment Rate</button>
        </>
        <hr className="section-divider" />
        <>
          <h2 className="section-heading">Logout</h2>
          <p className="section-description">Sign out of your account.</p>
          <button onClick={logout} className="action-button logout-button">
            Logout
          </button>
        </>
      </div>
    </div>
  );
};

export default Admin;